import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Avatar
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Chat as ChatIcon,
  Analytics as AnalyticsIcon,
  StarRate as StarRateIcon,
  SmartToy as SmartToyIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  AccountCircle,
  Logout,
  CallToAction as CallToActionIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import type { User } from 'firebase/auth';

interface BusinessConfig {
  businessName: string;
  welcomeMessage: string;
  chatPlaceholder: string;
  theme: {
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    textColor: string;
  };
  ai: {
    model: string;
    temperature: number;
    maxTokens: number;
    systemPrompt: string;
  };
  rateLimiting: {
    enabled: boolean;
    maxRequestsPerHour: number;
    maxRequestsPerDay: number;
  };
  notifications: {
    email: string;
    newChatAlert: boolean;
    dailyReport: boolean;
  };
  rag: {
    enabled: boolean;
    maxResults: number;
    similarityThreshold: number;
  };
}

interface LayoutProps {
  user: User;
  onLogout: () => void;
  children: React.ReactNode;
  config: BusinessConfig;
}

const drawerWidth = 240;

const Layout: React.FC<LayoutProps> = ({ user, onLogout, children, config }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleCloseMenu();
    onLogout();
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Conversations', icon: <ChatIcon />, path: '/conversations' },
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
    { text: 'Ratings', icon: <StarRateIcon />, path: '/ratings' },
    { text: 'AI Data', icon: <SmartToyIcon />, path: '/ai-data' },
    { text: 'CTA Buttons', icon: <CallToActionIcon />, path: '/cta-buttons' },
    { text: 'Status Messages', icon: <AssessmentIcon />, path: '/status' },
    { text: 'Availability Checker', icon: <ScheduleIcon />, path: '/availability' },
    { text: 'Voice AI', icon: <PhoneIcon />, path: '/voice-ai' },
    { text: 'Daily Reports', icon: <ScheduleIcon />, path: '/daily-reports' },
    { text: 'UI Settings', icon: <VisibilityIcon />, path: '/ui-settings' },
    { text: 'Test Email', icon: <EmailIcon />, path: '/test-email' },
    { text: 'Settings - Don\'t Touch', icon: <SettingsIcon />, path: '/settings' }
  ];

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          {config.businessName} Admin
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            selected={location.pathname === item.path}
            onClick={() => navigate(item.path)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` }
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {config.businessName} Admin Dashboard
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {user.email}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleProfileMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                <AccountCircle />
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1 }}>
          {children}
        </Box>
        
        {/* Footer */}
        <Box sx={{ mt: 'auto', py: 2, textAlign: 'center' }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ opacity: 0.7, fontSize: '0.75rem' }}
          >
            <a
              href="https://emertech.nz"
              target="_blank"
              rel="noopener noreferrer"
              style={{ 
                color: 'inherit', 
                textDecoration: 'none',
                borderBottom: '1px solid rgba(0,0,0,0.2)'
              }}
            >
              Powered by EmerTech AI
            </a>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout; 